import {
  getAuthoritiesPublicOptions,
  getOrganizationsSearchOptions,
  Roles,
} from "@aura-frontend/omnibus-az-client";
import { useQuery } from "@tanstack/react-query";
import { Select } from "@win-reality/react-ui";
import { useMemo } from "react";

import { useAppContext } from "@/hooks/use-app-context";
import { isMagnusAdminUser } from "@/lib/permissions";

interface OrganizationSelectProps {
  value: string | null;
  onChange: (value: string) => void;
}

function OrganizationSelect({ value, onChange }: OrganizationSelectProps) {
  const { user } = useAppContext();

  const isAdmin = isMagnusAdminUser((user?.roles ?? []) as Roles[]);

  const { data: orgData, isLoading: isLoadingOrganizations } = useQuery({
    ...getOrganizationsSearchOptions(),
    enabled: isAdmin,
  });

  const { data: authoritiesData, isLoading: isLoadingAuthorities } = useQuery({
    ...getAuthoritiesPublicOptions({
      baseUrl: "http://localhost:7071/api/",
    }),
    enabled: isAdmin,
  });

  const groupedItems = useMemo(() => {
    const groups: {
      label: string;
      items: { value: string; label: string }[];
    }[] = [];

    if (
      authoritiesData?.authorities &&
      authoritiesData.authorities.length > 0
    ) {
      groups.push({
        label: "Authorities",
        items: authoritiesData.authorities.map((authority) => ({
          value: authority.id!,
          label: authority.name!,
        })),
      });
    }

    if (orgData?.organizations && orgData.organizations.length > 0) {
      groups.push({
        label: "Organizations",
        items: orgData.organizations.map((org) => ({
          value: org.orgId,
          label: org.orgName,
        })),
      });
    }

    return groups;
  }, [authoritiesData, orgData]);

  if (!isAdmin) {
    return null;
  }

  return (
    <div className="w-60" data-testid="visibility-group-dropdown">
      <Select
        name="visibility-group"
        items={groupedItems}
        value={value ?? undefined}
        onValueChange={onChange}
        placeholder="Select a visibility group"
        isDisabled={isLoadingOrganizations || isLoadingAuthorities}
      />
    </div>
  );
}

export { OrganizationSelect };
